import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { getMeta, setMeta } from "./db";

const AuthContext = createContext(null);
const LS_USER_KEY = "vv_user";

// We treat these as "session is definitively invalid" → force sign-out.
const isAuthFailure = (err) => {
  const status = err?.response?.status;
  return status === 401 || status === 403;
};

const isUsableUser = (u) => u && typeof u === "object" && typeof u.user_id === "string" && typeof u.email === "string";

// Synchronous reads from localStorage so we can hydrate state on the very first render
function readSyncCachedUser() {
  try {
    const raw = localStorage.getItem(LS_USER_KEY);
    if (!raw) return null;
    const u = JSON.parse(raw);
    return isUsableUser(u) ? u : null;
  } catch (_) {
    return null;
  }
}

function writeCachedUser(u) {
  try {
    if (isUsableUser(u)) localStorage.setItem(LS_USER_KEY, JSON.stringify(u));
    else localStorage.removeItem(LS_USER_KEY);
  } catch (_) { /* ignore */ }
  // Mirror to IDB too (async, best-effort)
  setMeta("user", isUsableUser(u) ? u : null).catch(() => { /* ignore */ });
}

export const AuthProvider = ({ children }) => {
  // Hydrate from synchronous cache so a refresh while offline keeps the user signed in immediately
  const [user, setUserState] = useState(() => readSyncCachedUser());
  // If we already have a cached user, don't show the loading screen — render the app and revalidate in background
  const [loading, setLoading] = useState(() => !readSyncCachedUser());

  const setUser = useCallback((u) => {
    setUserState(u);
    writeCachedUser(u);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (!isUsableUser(data)) {
        // Non-JSON / SW shell / proxy edge case → keep current (cached) user
        return;
      }
      setUserState(data);
      writeCachedUser(data);
    } catch (err) {
      if (isAuthFailure(err)) {
        // Real auth failure → clear cached user and sign out
        writeCachedUser(null);
        setUserState(null);
      } else {
        // Network / offline / 503 → keep whatever user we already have
        // (either from sync localStorage cache or, as last resort, IDB)
        try {
          const idbUser = await getMeta("user");
          if (isUsableUser(idbUser)) {
            setUserState((cur) => (isUsableUser(cur) ? cur : idbUser));
          }
        } catch (_) { /* ignore */ }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* noop */ }
    localStorage.removeItem("session_token");
    writeCachedUser(null);
    setUserState(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
